import { useState, useMemo, useEffect, useCallback } from 'react';
import { X, Clock, Users, CheckCircle, AlertCircle, Settings, Sparkles, Plus, Trash2, RefreshCw, Link2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ServiceLevelParams } from './ServiceLevelControls';
import DogLoader from '../ui/DogLoader';
import { calculateStaffing } from '../../utils/erlangC';
import { useShiftOptimization } from '../../hooks/useShiftOptimization';

interface ShiftGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  forecastId: string;
  forecastData: any[];
  serviceLevelParams: ServiceLevelParams;
  onGenerateShifts: (config: ShiftGenerationConfig) => void;
  isLoading?: boolean;
  forecastStartDate?: string; // Optional: start date from forecast
  forecastEndDate?: string;   // Optional: end date from forecast
}

type ChannelType = 'PHONE' | 'CHAT' | 'EMAIL' | 'SMS' | 'SOCIAL';

interface ShiftGenerationConfig {
  startDate: string;
  endDate: string;
  shiftDuration: number; // hours
  breakDuration: number; // minutes
  coverageBuffer: number; // percentage
  shiftTypes: string[];
  customShifts: CustomShift[];
  teamId?: string;
  skillGroupId?: string;
  channels: ChannelType[]; // Multiple channels supported
  channel?: ChannelType; // Deprecated: use channels instead
  daysOfWeek: number[]; // 0=Sunday, 1=Monday, etc.
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun', fullLabel: 'Sunday' },
  { value: 1, label: 'Mon', fullLabel: 'Monday' },
  { value: 2, label: 'Tue', fullLabel: 'Tuesday' },
  { value: 3, label: 'Wed', fullLabel: 'Wednesday' },
  { value: 4, label: 'Thu', fullLabel: 'Thursday' },
  { value: 5, label: 'Fri', fullLabel: 'Friday' },
  { value: 6, label: 'Sat', fullLabel: 'Saturday' },
];

const CHANNELS = [
  { value: 'PHONE', label: 'Phone', color: 'bg-blue-100 text-blue-700' },
  { value: 'CHAT', label: 'Chat', color: 'bg-green-100 text-green-700' },
  { value: 'EMAIL', label: 'Email', color: 'bg-purple-100 text-purple-700' },
  { value: 'SMS', label: 'SMS', color: 'bg-orange-100 text-orange-700' },
  { value: 'SOCIAL', label: 'Social', color: 'bg-pink-100 text-pink-700' },
];

// Get channel display info
const getChannelInfo = (channel: string | undefined) => {
  const ch = CHANNELS.find(c => c.value === channel);
  return ch || CHANNELS[0]; // Default to PHONE if not specified
};

interface CustomShift {
  id: string;
  startTime: string; // HH:MM format (15-min intervals)
  duration: number; // hours
  agentsNeeded: number;
  isRecommended?: boolean;
  occupancyScore?: number;
  channel?: ChannelType; // Primary channel (deprecated, use channels)
  channels?: ChannelType[]; // Per-shift channels - if set, overrides global channels
}

interface ShiftTemplate {
  startTime: string;
  endTime: string;
  duration: number;
  agentsNeeded: number;
  coverage: { time: string; headcount: number }[];
  occupancyScore?: number;
}

// Generate 15-minute interval options
const generateTimeOptions = (): string[] => {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 15) {
      options.push(`${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Format time for display
const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Calculate end time from start time and duration
const calculateEndTime = (startTime: string, durationHours: number): string => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + durationHours * 60;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
};

// Days of week badge component
const DaysOfWeekBadge = ({ days }: { days: number[] }) => {
  const sortedDays = [...days].sort((a, b) => a - b);
  const isWeekdays = sortedDays.length === 5 && sortedDays.every((d, i) => d === i + 1);
  const isWeekends = sortedDays.length === 2 && sortedDays[0] === 0 && sortedDays[1] === 6;
  const isEveryDay = sortedDays.length === 7;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-xs text-gray-500">Days:</span>
      {isEveryDay ? (
        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
          Every day
        </span>
      ) : isWeekdays ? (
        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
          Weekdays
        </span>
      ) : isWeekends ? (
        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
          Weekends
        </span>
      ) : (
        <div className="flex gap-0.5">
          {DAYS_OF_WEEK.map(day => (
            <span
              key={day.value}
              className={`w-6 h-6 flex items-center justify-center text-[10px] font-medium rounded ${
                sortedDays.includes(day.value)
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {day.label.charAt(0)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ShiftGenerationModal({
  isOpen,
  onClose,
  forecastId: _forecastId,
  forecastData,
  serviceLevelParams,
  onGenerateShifts,
  isLoading = false,
  forecastStartDate,
  forecastEndDate,
}: ShiftGenerationModalProps) {
  // Calculate actual date range from forecast data if not provided via props
  const computedDateRange = useMemo(() => {
    // First try to use props if provided
    if (forecastStartDate && forecastEndDate) {
      return { startDate: forecastStartDate, endDate: forecastEndDate };
    }

    // Otherwise, derive from actual forecast data
    if (forecastData && forecastData.length > 0) {
      const timestamps = forecastData
        .filter(dp => dp.timestamp)
        .map(dp => new Date(dp.timestamp).getTime())
        .sort((a, b) => a - b);

      if (timestamps.length > 0) {
        const minDate = new Date(timestamps[0]);
        const maxDate = new Date(timestamps[timestamps.length - 1]);
        // Use local date format (YYYY-MM-DD) to match how filtering works
        // This ensures the date range encompasses all data points in local time
        const formatLocalDate = (d: Date) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        return {
          startDate: formatLocalDate(minDate),
          endDate: formatLocalDate(maxDate),
        };
      }
    }

    // Fallback to next 7 days
    return {
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    };
  }, [forecastData, forecastStartDate, forecastEndDate]);

  const [config, setConfig] = useState<ShiftGenerationConfig>({
    startDate: computedDateRange.startDate,
    endDate: computedDateRange.endDate,
    shiftDuration: 8,
    breakDuration: 60,
    coverageBuffer: 10,
    shiftTypes: [],
    customShifts: [],
    channels: ['PHONE'], // Default to PHONE, supports multiple channels
    channel: 'PHONE', // Deprecated: kept for backwards compatibility
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6], // Default to ALL days to match forecast data
  });

  // Update config dates when computed range changes (e.g., when modal opens with new data)
  useEffect(() => {
    setConfig(prev => ({
      ...prev,
      startDate: computedDateRange.startDate,
      endDate: computedDateRange.endDate,
    }));
  }, [computedDateRange.startDate, computedDateRange.endDate]);

  const [mode, setMode] = useState<'preset' | 'custom' | 'optimize'>('optimize');
  const [selectedShiftTypes, setSelectedShiftTypes] = useState<Set<string>>(new Set());
  const [editableOptimizedShifts, setEditableOptimizedShifts] = useState<CustomShift[]>([]);
  const [hasModifiedOptimized, setHasModifiedOptimized] = useState(false);
  const [targetShiftCount, setTargetShiftCount] = useState<number | null>(null); // null = use recommendation
  const [useBackendOptimization, setUseBackendOptimization] = useState(true); // Use backend API for optimization
  const [preferScheduleContinuity, setPreferScheduleContinuity] = useState(true); // Reference prior schedules

  // Backend shift optimization hook
  const {
    optimize,
    isOptimizing,
    optimizationResult,
    optimizationError,
    priorPatterns,
    isLoadingPriorPatterns,
    reset: resetOptimization,
  } = useShiftOptimization(_forecastId);

  // Fetch optimized shifts from backend when in optimize mode
  const fetchBackendOptimization = useCallback(async () => {
    if (!useBackendOptimization || mode !== 'optimize' || !_forecastId) return;

    try {
      const result = await optimize({
        targetOccupancy: 82.5,
        preferScheduleContinuity,
        channels: config.channels as any,
        daysOfWeek: config.daysOfWeek,
        shiftDuration: config.shiftDuration,
        coverageBuffer: config.coverageBuffer,
        startDate: config.startDate,
        endDate: config.endDate,
        maxShiftsPerDay: targetShiftCount ?? undefined,
        agentGroupingStrategy: 'BALANCED',
      });

      if (result?.shifts) {
        // Convert backend shifts to CustomShift format
        const convertedShifts: CustomShift[] = result.shifts.map((shift, index) => ({
          id: shift.id || `backend-${index}`,
          startTime: shift.startTime,
          duration: shift.duration,
          agentsNeeded: shift.agentsNeeded,
          isRecommended: shift.isRecommended,
          occupancyScore: shift.occupancyScore,
          channel: shift.channel,
        }));
        setEditableOptimizedShifts(convertedShifts);
        setHasModifiedOptimized(false);
      }
    } catch (error) {
      console.error('Backend optimization failed:', error);
      // Fall back to frontend optimization by not setting shifts
    }
  }, [optimize, useBackendOptimization, mode, _forecastId, preferScheduleContinuity, config, targetShiftCount]);

  // Auto-detect the data interval from forecast timestamps
  // This is critical: if data is hourly but we assume 15-min, Erlang C inflates agents by ~4×
  const detectedInterval = useMemo((): number => {
    if (!forecastData || forecastData.length < 2) return 15;

    const timestamps = forecastData
      .filter((dp: any) => dp.timestamp)
      .map((dp: any) => new Date(dp.timestamp).getTime())
      .sort((a: number, b: number) => a - b);

    if (timestamps.length < 2) return 15;

    // Calculate deltas between consecutive timestamps (in minutes)
    const deltaCounts = new Map<number, number>();
    const sampleSize = Math.min(timestamps.length - 1, 200);

    for (let i = 0; i < sampleSize; i++) {
      const deltaMinutes = (timestamps[i + 1] - timestamps[i]) / (1000 * 60);
      if (deltaMinutes > 0 && deltaMinutes <= 1440) {
        const rounded = Math.round(deltaMinutes);
        deltaCounts.set(rounded, (deltaCounts.get(rounded) || 0) + 1);
      }
    }

    if (deltaCounts.size === 0) return 15;

    // Find the most common delta
    let mostCommonDelta = 0;
    let maxCount = 0;
    deltaCounts.forEach((count, delta) => {
      if (count > maxCount) {
        maxCount = count;
        mostCommonDelta = delta;
      }
    });

    // Map to standard intervals
    if (mostCommonDelta <= 17) return 15;
    if (mostCommonDelta <= 45) return 30;
    if (mostCommonDelta <= 90) return 60;

    return mostCommonDelta;
  }, [forecastData]);

  // Calculate interval-level headcount from forecast data
  // Uses detected interval for accurate Erlang C calculations
  // Filters by selected date range and days of week
  const intervalHeadcount = useMemo((): Map<string, number> => {
    const headcountMap = new Map<string, number[]>();

    if (!forecastData || forecastData.length === 0) {
      return new Map<string, number>();
    }

    // Parse date range for filtering
    const startDate = config.startDate ? new Date(config.startDate + 'T00:00:00') : null;
    const endDate = config.endDate ? new Date(config.endDate + 'T23:59:59') : null;
    const selectedDays = new Set(config.daysOfWeek);

    forecastData.forEach((dp) => {
      if (!dp.timestamp || dp.predictedValue === undefined || dp.predictedValue === null) {
        return;
      }

      const date = new Date(dp.timestamp);

      // Filter by date range
      if (startDate && date < startDate) {
        return;
      }
      if (endDate && date > endDate) {
        return;
      }

      // Filter by days of week
      if (selectedDays.size > 0 && !selectedDays.has(date.getDay())) return;

      // Use the interval field if available (e.g., "09:00" or "09:00:00"), otherwise extract from timestamp
      let timeKey: string;
      if (dp.interval && typeof dp.interval === 'string') {
        // Parse interval field - could be "HH:MM" or "HH:MM:SS"
        const parts = dp.interval.split(':');
        const hour = parseInt(parts[0], 10);
        const minute = Math.floor(parseInt(parts[1] || '0', 10) / 15) * 15;
        timeKey = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      } else {
        // Fallback to extracting from timestamp
        const hour = date.getHours();
        const minute = Math.floor(date.getMinutes() / 15) * 15;
        timeKey = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      }

      if (!headcountMap.has(timeKey)) {
        headcountMap.set(timeKey, []);
      }

      // Calculate headcount using proper Erlang C formula with service level parameters
      // This accounts for AHT, service level targets, and shrinkage
      const staffingResult = calculateStaffing({
        callVolume: dp.predictedValue,
        ahtMinutes: serviceLevelParams.aht, // aht is already in minutes
        targetSLPercent: serviceLevelParams.slaPercent,
        targetAnswerSeconds: serviceLevelParams.answerTime,
        shrinkagePercent: serviceLevelParams.shrinkage,
        intervalMinutes: detectedInterval,
      });
      const headcount = staffingResult.requiredAgents;
      headcountMap.get(timeKey)!.push(headcount);
    });

    // Calculate average headcount per interval (across selected days)
    const avgHeadcount = new Map<string, number>();
    headcountMap.forEach((counts, time) => {
      const avg = counts.reduce((sum, c) => sum + c, 0) / counts.length;
      avgHeadcount.set(time, Math.ceil(avg));
    });

    return avgHeadcount;
  }, [forecastData, serviceLevelParams, config.startDate, config.endDate, config.daysOfWeek, detectedInterval]);

  // Calculate diagnostic summary to help users understand data being used
  const dataSummary = useMemo(() => {
    if (!forecastData || forecastData.length === 0) {
      return { dataPoints: 0, daysUsed: 0, peakHeadcount: 0, totalDataPoints: 0 };
    }

    const startDate = config.startDate ? new Date(config.startDate + 'T00:00:00') : null;
    const endDate = config.endDate ? new Date(config.endDate + 'T23:59:59') : null;
    const selectedDays = new Set(config.daysOfWeek);

    let matchingPoints = 0;
    const daysWithData = new Set<string>();
    let peakHeadcount = 0;

    forecastData.forEach((dp) => {
      if (!dp.timestamp || dp.predictedValue === undefined || dp.predictedValue === null) return;

      const date = new Date(dp.timestamp);

      // Filter by date range
      if (startDate && date < startDate) return;
      if (endDate && date > endDate) return;

      // Filter by days of week
      if (selectedDays.size > 0 && !selectedDays.has(date.getDay())) return;

      matchingPoints++;
      daysWithData.add(date.toISOString().split('T')[0]);

      // Calculate headcount for this point
      const staffingResult = calculateStaffing({
        callVolume: dp.predictedValue,
        ahtMinutes: serviceLevelParams.aht,
        targetSLPercent: serviceLevelParams.slaPercent,
        targetAnswerSeconds: serviceLevelParams.answerTime,
        shrinkagePercent: serviceLevelParams.shrinkage,
        intervalMinutes: detectedInterval,
      });
      peakHeadcount = Math.max(peakHeadcount, staffingResult.requiredAgents);
    });

    return {
      dataPoints: matchingPoints,
      daysUsed: daysWithData.size,
      peakHeadcount,
      totalDataPoints: forecastData.length,
    };
  }, [forecastData, serviceLevelParams, config.startDate, config.endDate, config.daysOfWeek, detectedInterval]);

  // Calculate recommended shift count based on forecast patterns
  // Analyzes demand curve to find distinct peak periods
  const recommendedShiftCount = useMemo((): number => {
    if (intervalHeadcount.size === 0) return 4; // Default fallback

    // Sort intervals by time
    const sortedIntervals = Array.from(intervalHeadcount.entries())
      .map(([time, demand]) => {
        const [hour, min] = time.split(':').map(Number);
        return { time, demand, minutes: hour * 60 + min };
      })
      .sort((a, b) => a.minutes - b.minutes);

    if (sortedIntervals.length === 0) return 4;

    // Calculate average and threshold for "significant" demand
    const demands = sortedIntervals.map(i => i.demand);
    const maxDemand = Math.max(...demands);
    const avgDemand = demands.reduce((a, b) => a + b, 0) / demands.length;
    const significantThreshold = Math.max(avgDemand * 0.5, maxDemand * 0.2);

    // Find distinct peak periods (periods of significant demand separated by gaps)
    let peakPeriods = 0;
    let inPeak = false;
    let lastPeakEnd = -1;
    const minGapMinutes = config.shiftDuration * 60 * 0.5; // Half a shift duration = gap

    for (const interval of sortedIntervals) {
      const isSignificant = interval.demand >= significantThreshold;

      if (isSignificant && !inPeak) {
        // Starting a new peak period
        if (lastPeakEnd === -1 || interval.minutes - lastPeakEnd > minGapMinutes) {
          peakPeriods++;
        }
        inPeak = true;
      } else if (!isSignificant && inPeak) {
        // Ending a peak period
        lastPeakEnd = interval.minutes;
        inPeak = false;
      }
    }

    // Also consider the span of hours with demand
    const hoursWithDemand = new Set(sortedIntervals.filter(i => i.demand > 0).map(i => Math.floor(i.minutes / 60)));
    const totalHoursWithDemand = hoursWithDemand.size;

    // Recommend based on coverage needs
    // Rule: approximately 1 shift per 6-8 hours of coverage needed, minimum 1, maximum 8
    const coverageBasedCount = Math.ceil(totalHoursWithDemand / config.shiftDuration);

    // Use the higher of peak-based or coverage-based, capped at reasonable limits
    // Increased max from 8 to 15 to allow more granular shift coverage
    const recommended = Math.max(1, Math.min(15, Math.max(peakPeriods, coverageBasedCount)));

    return recommended;
  }, [intervalHeadcount, config.shiftDuration]);

  // Effective shift count (user override or recommendation)
  const effectiveShiftCount = targetShiftCount ?? recommendedShiftCount;

  // Calculate occupancy score for a shift
  const calculateOccupancyScore = (startTime: string, durationHours: number, agents: number): number => {
    if (agents === 0) return 0;

    const [startHour, startMin] = startTime.split(':').map(Number);
    const startTotalMin = startHour * 60 + startMin;
    const endTotalMin = startTotalMin + durationHours * 60;

    let totalRequired = 0;
    let totalScheduled = 0;

    // Check each 15-minute interval in the shift
    for (let min = startTotalMin; min < endTotalMin; min += 15) {
      const hour = Math.floor((min % (24 * 60)) / 60);
      const minute = min % 60;
      const timeKey = `${hour.toString().padStart(2, '0')}:${(Math.floor(minute / 15) * 15).toString().padStart(2, '0')}`;

      const required = intervalHeadcount.get(timeKey) || 0;
      totalRequired += required;
      totalScheduled += agents;
    }

    if (totalScheduled === 0) return 0;

    // Occupancy = work / capacity (higher is better, but over 100% means understaffed)
    const occupancy = (totalRequired / totalScheduled) * 100;

    // Score: penalize both under and over utilization
    // Ideal occupancy is around 80-85%
    const idealOccupancy = 82.5;
    const deviation = Math.abs(occupancy - idealOccupancy);

    // Score from 0-100, where 100 is perfect occupancy
    return Math.max(0, 100 - deviation);
  };

  // Calculate agents needed for a shift based on forecast
  // This is used for single-shift calculations (custom mode, preset mode)
  const calculateAgentsForShift = (startTime: string, durationHours: number): number => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const startTotalMin = startHour * 60 + startMin;
    const endTotalMin = startTotalMin + durationHours * 60;

    let totalRequired = 0;
    let intervalCount = 0;

    // Calculate weighted average headcount during the shift
    // Weight by demand to give more importance to peak intervals
    for (let min = startTotalMin; min < endTotalMin; min += 15) {
      const hour = Math.floor((min % (24 * 60)) / 60);
      const minute = min % 60;
      const timeKey = `${hour.toString().padStart(2, '0')}:${(Math.floor(minute / 15) * 15).toString().padStart(2, '0')}`;

      const required = intervalHeadcount.get(timeKey) || 0;
      totalRequired += required;
      intervalCount++;
    }

    // Use average demand during shift, then apply buffer
    const avgRequired = intervalCount > 0 ? totalRequired / intervalCount : 0;
    return Math.ceil(avgRequired * (1 + config.coverageBuffer / 100));
  };

  // Calculate agents for multiple shifts, distributing agents to meet demand
  // without over-counting when shifts overlap
  const calculateDistributedAgents = (shifts: { startTime: string; duration: number }[]): Map<number, number> => {
    if (shifts.length === 0) return new Map();

    // Build coverage map: for each interval, which shifts are active
    const intervalCoverage = new Map<string, number[]>(); // timeKey -> array of shift indices

    shifts.forEach((shift, shiftIndex) => {
      const [startHour, startMin] = shift.startTime.split(':').map(Number);
      const startTotalMin = startHour * 60 + startMin;
      const endTotalMin = startTotalMin + shift.duration * 60;

      for (let min = startTotalMin; min < endTotalMin; min += 15) {
        const hour = Math.floor((min % (24 * 60)) / 60);
        const minute = min % 60;
        const timeKey = `${hour.toString().padStart(2, '0')}:${(Math.floor(minute / 15) * 15).toString().padStart(2, '0')}`;

        if (!intervalCoverage.has(timeKey)) {
          intervalCoverage.set(timeKey, []);
        }
        intervalCoverage.get(timeKey)!.push(shiftIndex);
      }
    });

    // For each shift, calculate its fair share of agents needed
    // At each interval, divide the demand among all active shifts
    const shiftAgentContributions = new Map<number, number[]>(); // shiftIndex -> array of agent contributions

    shifts.forEach((_, shiftIndex) => {
      shiftAgentContributions.set(shiftIndex, []);
    });

    intervalCoverage.forEach((activeShifts, timeKey) => {
      const demand = intervalHeadcount.get(timeKey) || 0;
      if (demand === 0 || activeShifts.length === 0) return;

      // Distribute demand among active shifts
      const agentsPerShift = demand / activeShifts.length;

      activeShifts.forEach((shiftIndex) => {
        shiftAgentContributions.get(shiftIndex)!.push(agentsPerShift);
      });
    });

    // For each shift, take the max of its contributions (to handle peak demand)
    // This ensures the shift is staffed for its peak responsibility
    const shiftAgents = new Map<number, number>();

    shiftAgentContributions.forEach((contributions, shiftIndex) => {
      if (contributions.length === 0) {
        shiftAgents.set(shiftIndex, 0);
      } else {
        // Use the 90th percentile to avoid over-staffing for brief spikes
        const sorted = [...contributions].sort((a, b) => a - b);
        const p90Index = Math.floor(sorted.length * 0.9);
        const p90Value = sorted[p90Index] || sorted[sorted.length - 1];

        // Apply coverage buffer
        const withBuffer = Math.ceil(p90Value * (1 + config.coverageBuffer / 100));
        shiftAgents.set(shiftIndex, withBuffer);
      }
    });

    return shiftAgents;
  };

  // Optimize shift start times to maximize occupancy
  const optimizedShifts = useMemo((): CustomShift[] => {
    if (mode !== 'optimize' || intervalHeadcount.size === 0) return [];

    const duration = config.shiftDuration;

    // Calculate minimum demand threshold (ignore intervals with very low demand)
    const allDemands = Array.from(intervalHeadcount.values());
    const maxDemand = Math.max(...allDemands);
    const minDemandThreshold = Math.max(1, Math.floor(maxDemand * 0.1)); // At least 10% of peak

    // Find peak demand periods and create shifts around them
    // Filter to intervals with meaningful demand (based on forecast data)
    const demandByTime = Array.from(intervalHeadcount.entries())
      .filter(([, demand]) => demand >= minDemandThreshold)
      .map(([time, demand]) => ({ time, demand }))
      .sort((a, b) => b.demand - a.demand);

    // Phase 1: Determine optimal shift start times
    const shiftStartTimes: string[] = [];
    const coveredTimes = new Set<string>();

    for (const { time } of demandByTime) {
      if (shiftStartTimes.length >= effectiveShiftCount) break;

      // Check if this time is already covered by an existing shift
      const [hour, min] = time.split(':').map(Number);
      const timeInMinutes = hour * 60 + min;

      let alreadyCovered = false;
      for (const coveredTime of coveredTimes) {
        const [cHour, cMin] = coveredTime.split(':').map(Number);
        const coveredInMinutes = cHour * 60 + cMin;
        // Consider covered if within 2 hours of an existing shift start
        if (Math.abs(timeInMinutes - coveredInMinutes) < 120) {
          alreadyCovered = true;
          break;
        }
      }

      if (alreadyCovered) continue;

      // Find optimal start time (shift should start 0-2 hours before peak)
      let bestStartTime = time;
      let bestScore = 0;

      // Try different start times (from 2 hours before to the peak time)
      for (let offset = -120; offset <= 0; offset += 15) {
        let startMinutes = timeInMinutes + offset;
        if (startMinutes < 0) startMinutes += 24 * 60;
        if (startMinutes >= 24 * 60) startMinutes -= 24 * 60;

        const startHour = Math.floor(startMinutes / 60);
        const startMin = startMinutes % 60;
        const testStartTime = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;

        // Use simple scoring for placement - we'll recalculate agents later
        const testAgents = calculateAgentsForShift(testStartTime, duration);
        const score = calculateOccupancyScore(testStartTime, duration, testAgents);

        if (score > bestScore) {
          bestScore = score;
          bestStartTime = testStartTime;
        }
      }

      shiftStartTimes.push(bestStartTime);
      coveredTimes.add(bestStartTime);
    }

    // Phase 2: Now that we have all shift times, distribute agents properly
    const shiftConfigs = shiftStartTimes.map(startTime => ({ startTime, duration }));
    const distributedAgents = calculateDistributedAgents(shiftConfigs);

    // Phase 3: Build the final shift objects with proper agent counts
    // Ensure each shift has at least 1 agent if there's any demand during its time
    const shifts: CustomShift[] = shiftStartTimes.map((startTime, index) => {
      let agents = distributedAgents.get(index) || 0;

      // If no agents were distributed but the shift was selected based on demand,
      // calculate agents directly from the shift's coverage period
      if (agents === 0) {
        agents = calculateAgentsForShift(startTime, duration);
      }

      // Ensure minimum 1 agent per shift template
      agents = Math.max(1, agents);

      const score = calculateOccupancyScore(startTime, duration, agents);

      return {
        id: `opt-${index}`,
        startTime,
        duration,
        agentsNeeded: agents,
        isRecommended: true,
        occupancyScore: Math.round(score),
      };
    });

    // Sort by start time
    return shifts.sort((a, b) => {
      const [aH, aM] = a.startTime.split(':').map(Number);
      const [bH, bM] = b.startTime.split(':').map(Number);
      return (aH * 60 + aM) - (bH * 60 + bM);
    });
  }, [mode, intervalHeadcount, config.shiftDuration, config.coverageBuffer, effectiveShiftCount]);

  // Sync editable optimized shifts when AI recommendations change (only if not modified)
  // Use backend optimization when enabled, otherwise fall back to frontend
  useEffect(() => {
    if (mode !== 'optimize') return;

    if (useBackendOptimization && _forecastId) {
      // Trigger backend optimization
      fetchBackendOptimization();
    } else if (!hasModifiedOptimized && optimizedShifts.length > 0) {
      // Fall back to frontend optimization
      setEditableOptimizedShifts(optimizedShifts);
    }
  }, [mode, useBackendOptimization, _forecastId, fetchBackendOptimization, optimizedShifts, hasModifiedOptimized]);

  // Reset modification flag and re-fetch when switching modes or key config changes
  useEffect(() => {
    if (mode !== 'optimize') {
      setHasModifiedOptimized(false);
      resetOptimization();
    }
  }, [mode, resetOptimization]);

  // Functions to edit optimized shifts
  const updateOptimizedShift = (id: string, updates: Partial<CustomShift>) => {
    setHasModifiedOptimized(true);
    setEditableOptimizedShifts((prev) =>
      prev.map((shift) => {
        if (shift.id !== id) return shift;
        const updated = { ...shift, ...updates };
        // Recalculate agents and occupancy if time or duration changed
        if (updates.startTime !== undefined || updates.duration !== undefined) {
          updated.agentsNeeded = calculateAgentsForShift(updated.startTime, updated.duration);
          updated.occupancyScore = Math.round(
            calculateOccupancyScore(updated.startTime, updated.duration, updated.agentsNeeded)
          );
        }
        return updated;
      })
    );
  };

  const removeOptimizedShift = (id: string) => {
    setHasModifiedOptimized(true);
    setEditableOptimizedShifts((prev) => prev.filter((shift) => shift.id !== id));
  };

  const addOptimizedShift = () => {
    setHasModifiedOptimized(true);
    const newId = `opt-manual-${Date.now()}`;
    const newShift: CustomShift = {
      id: newId,
      startTime: '09:00',
      duration: config.shiftDuration,
      agentsNeeded: calculateAgentsForShift('09:00', config.shiftDuration),
      isRecommended: false,
      occupancyScore: Math.round(calculateOccupancyScore('09:00', config.shiftDuration, calculateAgentsForShift('09:00', config.shiftDuration))),
    };
    setEditableOptimizedShifts((prev) => [...prev, newShift].sort((a, b) => {
      const [aH, aM] = a.startTime.split(':').map(Number);
      const [bH, bM] = b.startTime.split(':').map(Number);
      return (aH * 60 + aM) - (bH * 60 + bM);
    }));
  };

  const resetOptimizedShifts = () => {
    setHasModifiedOptimized(false);
    setEditableOptimizedShifts(optimizedShifts);
  };

  // Generate coverage data for a shift
  const generateCoverageData = (startTime: string, durationHours: number): { time: string; headcount: number }[] => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const startTotalMin = startHour * 60 + startMin;
    const endTotalMin = startTotalMin + durationHours * 60;

    const coverage: { time: string; headcount: number }[] = [];

    for (let min = startTotalMin; min < endTotalMin; min += 15) {
      const hour = Math.floor((min % (24 * 60)) / 60);
      const minute = min % 60;
      const timeKey = `${hour.toString().padStart(2, '0')}:${(Math.floor(minute / 15) * 15).toString().padStart(2, '0')}`;

      coverage.push({
        time: timeKey,
        headcount: intervalHeadcount.get(timeKey) || 0,
      });
    }

    return coverage;
  };

  // Calculate shift templates based on preset types
  const presetShiftTemplates = useMemo((): ShiftTemplate[] => {
    if (mode !== 'preset' || selectedShiftTypes.size === 0) return [];

    const templates: ShiftTemplate[] = [];

    const presetDefinitions: Record<string, { start: string; end: string }> = {
      'MORNING': { start: '07:00', end: '15:00' },
      'MID_DAY': { start: '09:00', end: '17:00' },
      'AFTERNOON': { start: '11:00', end: '19:00' },
      'EVENING': { start: '15:00', end: '23:00' },
      'OVERNIGHT': { start: '23:00', end: '07:00' },
    };

    selectedShiftTypes.forEach((type) => {
      const def = presetDefinitions[type];
      if (!def) return;

      const agents = calculateAgentsForShift(def.start, 8);
      const coverage = generateCoverageData(def.start, 8);
      const occupancy = calculateOccupancyScore(def.start, 8, agents);

      templates.push({
        startTime: def.start,
        endTime: def.end,
        duration: 8,
        agentsNeeded: agents,
        coverage,
        occupancyScore: Math.round(occupancy),
      });
    });

    return templates;
  }, [mode, selectedShiftTypes, intervalHeadcount, config.coverageBuffer]);

  // Add custom shift
  const addCustomShift = () => {
    const newShift: CustomShift = {
      id: `custom-${Date.now()}`,
      startTime: '09:00',
      duration: config.shiftDuration,
      agentsNeeded: calculateAgentsForShift('09:00', config.shiftDuration),
    };
    setConfig({
      ...config,
      customShifts: [...config.customShifts, newShift],
    });
  };

  // Update custom shift
  const updateCustomShift = (id: string, updates: Partial<CustomShift>) => {
    setConfig({
      ...config,
      customShifts: config.customShifts.map((shift) => {
        if (shift.id !== id) return shift;

        const updated = { ...shift, ...updates };

        // Recalculate agents if start time or duration changed
        if (updates.startTime !== undefined || updates.duration !== undefined) {
          updated.agentsNeeded = calculateAgentsForShift(
            updated.startTime,
            updated.duration
          );
          updated.occupancyScore = Math.round(
            calculateOccupancyScore(updated.startTime, updated.duration, updated.agentsNeeded)
          );
        }

        return updated;
      }),
    });
  };

  // Remove custom shift
  const removeCustomShift = (id: string) => {
    setConfig({
      ...config,
      customShifts: config.customShifts.filter((shift) => shift.id !== id),
    });
  };

  const toggleShiftType = (type: string) => {
    const newSelected = new Set(selectedShiftTypes);
    if (newSelected.has(type)) {
      newSelected.delete(type);
    } else {
      newSelected.add(type);
    }
    setSelectedShiftTypes(newSelected);
  };

  // Get current shifts based on mode
  const currentShifts = useMemo((): CustomShift[] => {
    switch (mode) {
      case 'optimize':
        return editableOptimizedShifts;
      case 'custom':
        return config.customShifts;
      case 'preset':
        return presetShiftTemplates.map((t) => ({
          id: t.startTime,
          startTime: t.startTime,
          duration: t.duration,
          agentsNeeded: t.agentsNeeded,
          occupancyScore: t.occupancyScore,
          channel: config.channel, // Use global channel for preset shifts
        }));
      default:
        return [];
    }
  }, [mode, editableOptimizedShifts, config.customShifts, presetShiftTemplates, config.channel]);

  const handleGenerate = () => {
    if (currentShifts.length === 0) {
      alert('Please configure at least one shift');
      return;
    }

    const configWithShifts: ShiftGenerationConfig = {
      ...config,
      shiftTypes: mode === 'preset' ? Array.from(selectedShiftTypes) : [],
      customShifts: currentShifts,
    };

    // Don't close modal here - parent will close on success
    // This allows the loading state (DogLoader) to be shown while generating
    onGenerateShifts(configWithShifts);
  };

  const avgOccupancy = currentShifts.length > 0
    ? Math.round(currentShifts.reduce((sum, s) => sum + (s.occupancyScore || 0), 0) / currentShifts.length)
    : 0;

  // Calculate peak concurrent agents (actual agents needed at any one time)
  // This accounts for shift overlaps - if 2 shifts overlap, their agents both contribute at that time
  const peakConcurrentAgents = useMemo(() => {
    if (currentShifts.length === 0) return 0;

    // Build a map of agents per 15-min interval
    const intervalAgents = new Map<string, number>();

    currentShifts.forEach((shift) => {
      const [startHour, startMin] = shift.startTime.split(':').map(Number);
      const startTotalMin = startHour * 60 + startMin;
      const endTotalMin = startTotalMin + shift.duration * 60;

      for (let min = startTotalMin; min < endTotalMin; min += 15) {
        const hour = Math.floor((min % (24 * 60)) / 60);
        const minute = min % 60;
        const timeKey = `${hour.toString().padStart(2, '0')}:${(Math.floor(minute / 15) * 15).toString().padStart(2, '0')}`;

        const current = intervalAgents.get(timeKey) || 0;
        intervalAgents.set(timeKey, current + shift.agentsNeeded);
      }
    });

    // Find the maximum across all intervals
    let maxAgents = 0;
    intervalAgents.forEach((agents) => {
      maxAgents = Math.max(maxAgents, agents);
    });

    return maxAgents;
  }, [currentShifts]);

  // Calculate shift counts for display
  const shiftCounts = useMemo(() => {
    if (currentShifts.length === 0) return { grouped: 0, total: 0, daysCount: 0 };

    // Parse dates as LOCAL time to match how backend processes them
    // Using 'T12:00:00' to avoid any timezone edge cases at midnight
    const [startYear, startMonth, startDay] = config.startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = config.endDate.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    let daysCount = 0;

    // Count days in range that match selected days of week
    // Using a fresh Date for iteration to avoid mutation issues
    const current = new Date(start);
    while (current <= end) {
      if (config.daysOfWeek.includes(current.getDay())) {
        daysCount++;
      }
      current.setDate(current.getDate() + 1);
    }

    // Grouped shifts = shift templates × days (each shift can have multiple agents)
    const groupedShifts = currentShifts.length * daysCount;

    // Total includes channel multiplier if generating for multiple channels
    const channelMultiplier = config.channels.length;
    const totalShifts = groupedShifts * channelMultiplier;

    return { grouped: groupedShifts, total: totalShifts, daysCount };
  }, [currentShifts.length, config.startDate, config.endDate, config.daysOfWeek, config.channels.length]);

  const shiftTypeOptions = [
    { value: 'MORNING', label: 'Morning', time: '7:00 AM - 3:00 PM' },
    { value: 'MID_DAY', label: 'Mid-Day', time: '9:00 AM - 5:00 PM' },
    { value: 'AFTERNOON', label: 'Afternoon', time: '11:00 AM - 7:00 PM' },
    { value: 'EVENING', label: 'Evening', time: '3:00 PM - 11:00 PM' },
    { value: 'OVERNIGHT', label: 'Overnight', time: '11:00 PM - 7:00 AM' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Generate Shifts from Forecast</h2>
                    <p className="text-indigo-100 text-sm">
                      Optimize shift times to maximize agent occupancy
                    </p>
                  </div>
                </div>
                {!isLoading && (
                  <button
                    onClick={onClose}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                )}
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-16">
                <DogLoader message="Generating shifts from forecast..." />
                <p className="text-gray-500 text-sm mt-4">
                  This may take a moment depending on the date range
                </p>
              </div>
            )}

            {/* Content */}
            <div className={`p-6 overflow-y-auto max-h-[calc(90vh-220px)] ${isLoading ? 'hidden' : ''}`}>
              {/* Simplified Single-Flow Header */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Smart Shift Generation</h3>
                      <p className="text-sm text-gray-600">
                        Shifts are automatically optimized for your forecast. Adjust below if needed.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setMode(mode === 'optimize' ? 'custom' : 'optimize')}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    {mode === 'optimize' ? 'Switch to Manual' : 'Switch to Auto'}
                  </button>
                </div>
              </div>

              {/* Configuration Section */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Settings className="h-5 w-5 text-indigo-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-indigo-900 mb-3">Shift Configuration</h3>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={config.startDate}
                          onChange={(e) => setConfig({ ...config, startDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={config.endDate}
                          onChange={(e) => setConfig({ ...config, endDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Shift Duration (hours)
                        </label>
                        <select
                          value={config.shiftDuration}
                          onChange={(e) => setConfig({ ...config, shiftDuration: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          {[4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12].map((d) => (
                            <option key={d} value={d}>{d} hours</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Break Duration (min)
                        </label>
                        <select
                          value={config.breakDuration}
                          onChange={(e) => setConfig({ ...config, breakDuration: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          {[0, 15, 30, 45, 60, 75, 90, 105, 120].map((d) => (
                            <option key={d} value={d}>{d} min</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Coverage Buffer (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="50"
                          value={config.coverageBuffer}
                          onChange={(e) => setConfig({ ...config, coverageBuffer: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      {mode === 'optimize' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Shift Templates
                          </label>
                          <div className="flex items-center gap-2">
                            <select
                              value={targetShiftCount ?? 'auto'}
                              onChange={(e) => {
                                const val = e.target.value;
                                setTargetShiftCount(val === 'auto' ? null : parseInt(val));
                                setHasModifiedOptimized(false); // Reset to recalculate
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                              <option value="auto">Auto ({recommendedShiftCount})</option>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15].map((n) => (
                                <option key={n} value={n}>{n} shift{n !== 1 ? 's' : ''}</option>
                              ))}
                            </select>
                          </div>
                          {targetShiftCount === null && (
                            <p className="text-xs text-gray-500 mt-1">
                              Based on {intervalHeadcount.size > 0 ? 'demand patterns' : 'default'}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Backend Optimization Toggle - Only in Optimize Mode */}
                    {mode === 'optimize' && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-900">Advanced Optimization</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setUseBackendOptimization(!useBackendOptimization);
                              setHasModifiedOptimized(false); // Trigger recalculation
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              useBackendOptimization ? 'bg-indigo-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                useBackendOptimization ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {useBackendOptimization
                            ? 'Using server-side interval-aligned optimization with gap analysis'
                            : 'Using client-side heuristic optimization (faster but less precise)'}
                        </p>
                      </div>
                    )}

                    {/* Schedule Continuity Toggle - Only in Optimize Mode with Backend */}
                    {mode === 'optimize' && useBackendOptimization && (
                      <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Link2 className="h-4 w-4 text-indigo-600" />
                            <span className="text-sm font-medium text-indigo-900">Schedule Continuity</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setPreferScheduleContinuity(!preferScheduleContinuity);
                              setHasModifiedOptimized(false); // Trigger re-fetch
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              preferScheduleContinuity ? 'bg-indigo-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                preferScheduleContinuity ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        <p className="text-xs text-indigo-700 mt-1">
                          {preferScheduleContinuity
                            ? 'Reference prior shift patterns to maintain agent schedule consistency'
                            : 'Generate shifts without considering prior schedules'}
                        </p>
                        {preferScheduleContinuity && priorPatterns.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            <span className="text-xs text-indigo-600">Prior patterns:</span>
                            {priorPatterns.slice(0, 3).map((pattern, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded">
                                {pattern.startTime} ({pattern.duration}h)
                              </span>
                            ))}
                            {priorPatterns.length > 3 && (
                              <span className="text-xs text-indigo-500">+{priorPatterns.length - 3} more</span>
                            )}
                          </div>
                        )}
                        {preferScheduleContinuity && isLoadingPriorPatterns && (
                          <p className="text-xs text-indigo-500 mt-1">Loading prior patterns...</p>
                        )}
                      </div>
                    )}

                    {/* Channel Selection - Multi-select */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Channels
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {CHANNELS.map((ch) => (
                          <button
                            key={ch.value}
                            type="button"
                            onClick={() => {
                              const newChannels = config.channels.includes(ch.value as ChannelType)
                                ? config.channels.filter((c) => c !== ch.value)
                                : [...config.channels, ch.value as ChannelType];
                              // Ensure at least one channel is selected
                              if (newChannels.length > 0) {
                                setConfig({ ...config, channels: newChannels, channel: newChannels[0] });
                              }
                            }}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                              config.channels.includes(ch.value as ChannelType)
                                ? ch.color.replace('100', '600').replace('700', 'white') + ' ring-2 ring-offset-1 ring-current'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            style={config.channels.includes(ch.value as ChannelType) ? {
                              backgroundColor: ch.value === 'PHONE' ? '#2563eb' :
                                               ch.value === 'CHAT' ? '#16a34a' :
                                               ch.value === 'EMAIL' ? '#9333ea' :
                                               ch.value === 'SMS' ? '#ea580c' : '#ec4899',
                              color: 'white'
                            } : {}}
                          >
                            {ch.label}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Select multiple channels to create shifts across all selected channels
                      </p>
                    </div>

                    {/* Days of Week Selection */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Days of Week
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {DAYS_OF_WEEK.map((day) => (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() => {
                              const newDays = config.daysOfWeek.includes(day.value)
                                ? config.daysOfWeek.filter((d) => d !== day.value)
                                : [...config.daysOfWeek, day.value].sort((a, b) => a - b);
                              setConfig({ ...config, daysOfWeek: newDays });
                            }}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                              config.daysOfWeek.includes(day.value)
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => setConfig({ ...config, daysOfWeek: [1, 2, 3, 4, 5] })}
                          className="text-xs text-indigo-600 hover:text-indigo-800"
                        >
                          Weekdays
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfig({ ...config, daysOfWeek: [0, 6] })}
                          className="text-xs text-indigo-600 hover:text-indigo-800"
                        >
                          Weekends
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfig({ ...config, daysOfWeek: [0, 1, 2, 3, 4, 5, 6] })}
                          className="text-xs text-indigo-600 hover:text-indigo-800"
                        >
                          All Days
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Summary - helps users understand what data is being used */}
              <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center space-x-6 text-sm">
                    <div>
                      <span className="text-gray-500">Forecast data: </span>
                      <span className="font-semibold text-gray-900">{dataSummary.dataPoints.toLocaleString()}</span>
                      <span className="text-gray-400 text-xs ml-1">
                        intervals across {dataSummary.daysUsed} days
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Peak demand: </span>
                      <span className="font-bold text-indigo-600">{dataSummary.peakHeadcount}</span>
                      <span className="text-gray-400 text-xs ml-1">agents/interval</span>
                    </div>
                    {currentShifts.length > 0 && (
                      <div>
                        <span className="text-gray-500">Shifts: </span>
                        <span className="font-bold text-indigo-600">{shiftCounts.grouped}</span>
                        <span className="text-gray-400 text-xs ml-1">
                          ({currentShifts.length} template{currentShifts.length !== 1 ? 's' : ''} × {shiftCounts.daysCount} day{shiftCounts.daysCount !== 1 ? 's' : ''})
                        </span>
                      </div>
                    )}
                    {intervalHeadcount.size > 0 && (
                      <div>
                        <span className="text-gray-500">Demand hours: </span>
                        <span className="font-semibold text-gray-900">
                          {(() => {
                            const times = Array.from(intervalHeadcount.keys()).sort();
                            if (times.length === 0) return 'None';
                            return `${times[0]} - ${times[times.length - 1]}`;
                          })()}
                        </span>
                        <span className="text-gray-400 text-xs ml-1">
                          ({intervalHeadcount.size} intervals)
                        </span>
                      </div>
                    )}
                  </div>
                  {dataSummary.dataPoints === 0 && dataSummary.totalDataPoints > 0 && (
                    <div className="text-amber-600 text-sm font-medium">
                      No data matches selected filters
                    </div>
                  )}
                  {intervalHeadcount.size === 0 && dataSummary.dataPoints > 0 && (
                    <div className="text-amber-600 text-sm font-medium">
                      No interval data available - check forecast interval format
                    </div>
                  )}
                </div>
              </div>

              {/* Mode-specific content */}
              {mode === 'preset' && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Select Shift Types</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {shiftTypeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => toggleShiftType(option.value)}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          selectedShiftTypes.has(option.value)
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-gray-900">{option.label}</span>
                          {selectedShiftTypes.has(option.value) && (
                            <CheckCircle className="h-5 w-5 text-indigo-600" />
                          )}
                        </div>
                        <div className="text-xs text-gray-600 mb-2">{option.time}</div>
                        {selectedShiftTypes.has(option.value) && (
                          <DaysOfWeekBadge days={config.daysOfWeek} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {mode === 'custom' && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Custom Shift Times</h3>
                    <button
                      onClick={addCustomShift}
                      className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Shift</span>
                    </button>
                  </div>

                  {config.customShifts.length === 0 ? (
                    <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">No custom shifts defined</p>
                      <button
                        onClick={addCustomShift}
                        className="text-indigo-600 font-medium hover:text-indigo-700"
                      >
                        Add your first shift
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {config.customShifts.map((shift) => (
                        <div key={shift.id} className="bg-white border-2 border-gray-200 rounded-lg p-4">
                          <div className="flex items-center space-x-4">
                            <div className="flex-1">
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Start Time (15-min intervals)
                              </label>
                              <select
                                value={shift.startTime}
                                onChange={(e) => updateCustomShift(shift.id, { startTime: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                              >
                                {TIME_OPTIONS.map((time) => (
                                  <option key={time} value={time}>
                                    {formatTime(time)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="flex-1">
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Duration
                              </label>
                              <select
                                value={shift.duration}
                                onChange={(e) => updateCustomShift(shift.id, { duration: parseFloat(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                              >
                                {[4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12].map((d) => (
                                  <option key={d} value={d}>{d} hrs</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex-1">
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Channel
                              </label>
                              <select
                                value={shift.channel || config.channel || 'PHONE'}
                                onChange={(e) => updateCustomShift(shift.id, { channel: e.target.value as any })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                              >
                                {CHANNELS.map((ch) => (
                                  <option key={ch.value} value={ch.value}>{ch.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="w-20 text-center">
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Agents
                              </label>
                              <div className="text-xl font-bold text-indigo-600">
                                {shift.agentsNeeded}
                              </div>
                            </div>
                            <div className="w-20 text-center">
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Occupancy
                              </label>
                              <div className={`text-xl font-bold ${
                                (shift.occupancyScore || 0) >= 70 ? 'text-green-600' :
                                (shift.occupancyScore || 0) >= 50 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {shift.occupancyScore || 0}%
                              </div>
                            </div>
                            <button
                              onClick={() => removeCustomShift(shift.id)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">
                                {formatTime(shift.startTime)} - {formatTime(calculateEndTime(shift.startTime, shift.duration))}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getChannelInfo(shift.channel || config.channel).color}`}>
                                {getChannelInfo(shift.channel || config.channel).label}
                              </span>
                            </div>
                            <DaysOfWeekBadge days={config.daysOfWeek} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {mode === 'optimize' && (
                <div className="mb-6">
                  {/* Backend Optimization Loading State */}
                  {isOptimizing && (
                    <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <RefreshCw className="h-5 w-5 text-indigo-600 animate-spin" />
                        <div>
                          <p className="font-medium text-indigo-900">Optimizing shifts...</p>
                          <p className="text-sm text-indigo-700">
                            Analyzing forecast demand and calculating optimal shift coverage
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Backend Optimization Error State */}
                  {optimizationError && !isOptimizing && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-900">Optimization Error</p>
                          <p className="text-sm text-red-700">{optimizationError.message}</p>
                          <button
                            onClick={() => {
                              resetOptimization();
                              setUseBackendOptimization(false);
                            }}
                            className="mt-2 text-sm text-red-600 underline hover:text-red-800"
                          >
                            Use frontend optimization instead
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">AI-Recommended Shifts</h3>
                      <p className="text-sm text-gray-600">
                        {isOptimizing
                          ? 'Fetching optimized shifts from server...'
                          : hasModifiedOptimized
                          ? 'Modified from AI recommendations - click Reset to restore'
                          : `Generating ${effectiveShiftCount} shift${effectiveShiftCount !== 1 ? 's' : ''} optimized for maximum occupancy`}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {hasModifiedOptimized && (
                        <button
                          onClick={resetOptimizedShifts}
                          className="px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                        >
                          Reset to AI
                        </button>
                      )}
                      <button
                        onClick={addOptimizedShift}
                        className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                      >
                        <Plus className="h-3 w-3" />
                        <span>Add Shift</span>
                      </button>
                      {editableOptimizedShifts.length > 0 && (
                        <>
                          <div className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                            {editableOptimizedShifts.reduce((sum, s) => sum + s.agentsNeeded, 0)} total agents
                          </div>
                          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                            Avg. Occupancy: {avgOccupancy}%
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {editableOptimizedShifts.length === 0 && optimizedShifts.length === 0 ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                      <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-3" />
                      <h3 className="font-semibold text-yellow-900 mb-1">No Forecast Data</h3>
                      <p className="text-sm text-yellow-700">
                        Unable to optimize shifts without forecast data. Please ensure the forecast has data points.
                      </p>
                    </div>
                  ) : editableOptimizedShifts.length === 0 ? (
                    <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">All shifts removed</p>
                      <button
                        onClick={resetOptimizedShifts}
                        className="text-indigo-600 font-medium hover:text-indigo-700"
                      >
                        Reset to AI recommendations
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {editableOptimizedShifts.map((shift, index) => {
                        const coverage = generateCoverageData(shift.startTime, shift.duration);
                        // Use global peak headcount for consistent scaling across all shift charts
                        const maxHeadcount = Math.max(dataSummary.peakHeadcount, 1);

                        return (
                          <div key={shift.id} className={`bg-white border-2 rounded-lg p-4 ${shift.isRecommended ? 'border-indigo-200' : 'border-gray-200'}`}>
                            <div className="flex items-center space-x-4 mb-3">
                              <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-indigo-600 font-bold text-sm">{index + 1}</span>
                              </div>
                              <div className="flex-1 grid grid-cols-4 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    Start Time
                                  </label>
                                  <select
                                    value={shift.startTime}
                                    onChange={(e) => updateOptimizedShift(shift.id, { startTime: e.target.value })}
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  >
                                    {TIME_OPTIONS.map((time) => (
                                      <option key={time} value={time}>
                                        {formatTime(time)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    Duration
                                  </label>
                                  <select
                                    value={shift.duration}
                                    onChange={(e) => updateOptimizedShift(shift.id, { duration: parseFloat(e.target.value) })}
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  >
                                    {[4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12].map((d) => (
                                      <option key={d} value={d}>{d} hrs</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    Channel
                                  </label>
                                  <select
                                    value={shift.channel || config.channel || 'PHONE'}
                                    onChange={(e) => updateOptimizedShift(shift.id, { channel: e.target.value as any })}
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  >
                                    {CHANNELS.map((ch) => (
                                      <option key={ch.value} value={ch.value}>{ch.label}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-indigo-600 mb-1">
                                    <Users className="inline h-3 w-3 mr-1" />
                                    Agents Needed
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={shift.agentsNeeded}
                                    onChange={(e) => updateOptimizedShift(shift.id, { agentsNeeded: parseInt(e.target.value) || 1 })}
                                    className="w-full px-2 py-1.5 border-2 border-indigo-300 rounded-md text-sm font-semibold text-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-indigo-50"
                                  />
                                </div>
                              </div>
                              <div className="text-center w-20 flex-shrink-0">
                                <div className={`text-lg font-bold ${
                                  (shift.occupancyScore || 0) >= 70 ? 'text-green-600' :
                                  (shift.occupancyScore || 0) >= 50 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                  {shift.occupancyScore}%
                                </div>
                                <div className="text-xs text-gray-500">occupancy</div>
                              </div>
                              <button
                                onClick={() => removeOptimizedShift(shift.id)}
                                className="p-2 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>

                            {/* Coverage Chart */}
                            <div className="bg-gray-50 rounded p-3">
                              <div className="flex items-center justify-between text-xs font-medium text-gray-700 mb-2">
                                <span>Avg. Demand During Shift (15-min intervals)</span>
                                <span className="text-gray-500">
                                  {formatTime(shift.startTime)} - {formatTime(calculateEndTime(shift.startTime, shift.duration))}
                                  <span className="ml-2 text-indigo-600">Peak: {Math.max(...coverage.map(c => c.headcount), 0)}</span>
                                </span>
                              </div>
                              <div className="flex items-end space-x-0.5 h-10">
                                {coverage.map((cov, i) => (
                                  <div key={i} className="flex-1 flex flex-col items-center">
                                    <div
                                      className="w-full bg-indigo-400 rounded-t"
                                      style={{
                                        height: `${(cov.headcount / maxHeadcount) * 100}%`,
                                        minHeight: cov.headcount > 0 ? '4px' : '0',
                                      }}
                                    ></div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="mt-2 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {shift.isRecommended && (
                                  <div className="flex items-center text-xs text-indigo-600">
                                    <Sparkles className="h-3 w-3 mr-1" />
                                    AI Recommended
                                  </div>
                                )}
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getChannelInfo(shift.channel || config.channel).color}`}>
                                  {getChannelInfo(shift.channel || config.channel).label}
                                </span>
                              </div>
                              <DaysOfWeekBadge days={config.daysOfWeek} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Backend Optimization Results - Enhanced Dashboard */}
                  {optimizationResult && !isOptimizing && (
                    <div className="mt-4 space-y-4">
                      {/* Staffing Efficiency - Single Unified Metric */}
                      {optimizationResult.summary && (
                        <div className={`p-4 rounded-lg border ${
                          (optimizationResult.summary.staffingEfficiency || 0) >= 75 && (optimizationResult.summary.staffingEfficiency || 0) <= 90
                            ? 'bg-green-50 border-green-200'
                            : (optimizationResult.summary.staffingEfficiency || 0) < 75
                              ? 'bg-amber-50 border-amber-200'
                              : 'bg-red-50 border-red-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900">Staffing Efficiency</h4>
                              <p className="text-xs text-gray-600 mt-0.5">
                                {(optimizationResult.summary.staffingEfficiency || 0) < 75
                                  ? 'Overstaffed - consider reducing shifts'
                                  : (optimizationResult.summary.staffingEfficiency || 0) > 90
                                    ? 'Understaffed - SLA risk'
                                    : 'Optimal staffing level'}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`text-3xl font-bold ${
                                (optimizationResult.summary.staffingEfficiency || 0) >= 75 && (optimizationResult.summary.staffingEfficiency || 0) <= 90
                                  ? 'text-green-600'
                                  : (optimizationResult.summary.staffingEfficiency || 0) < 75
                                    ? 'text-amber-600'
                                    : 'text-red-600'
                              }`}>
                                {optimizationResult.summary.staffingEfficiency?.toFixed(0) || 0}%
                              </span>
                              <p className="text-xs text-gray-500">75-90% optimal</p>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
                            <div className="text-center p-2 bg-white/50 rounded">
                              <div className="font-semibold text-gray-900">{optimizationResult.summary.totalAgents}</div>
                              <div className="text-gray-600">Total Agents</div>
                            </div>
                            <div className="text-center p-2 bg-white/50 rounded">
                              <div className="font-semibold text-gray-900">{optimizationResult.summary.peakConcurrentAgents}</div>
                              <div className="text-gray-600">Peak Concurrent</div>
                            </div>
                            <div className="text-center p-2 bg-white/50 rounded">
                              <div className="font-semibold text-gray-900">{optimizationResult.summary.coveragePercentage?.toFixed(0) || 0}%</div>
                              <div className="text-gray-600">Coverage</div>
                            </div>
                            <div className="text-center p-2 bg-white/50 rounded">
                              <div className="font-semibold text-gray-900">{optimizationResult.summary.averageOccupancy?.toFixed(0) || 0}%</div>
                              <div className="text-gray-600">Occupancy</div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        {/* Channel Balance */}
                        {optimizationResult.channelBalance && optimizationResult.channelBalance.length > 0 && (
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3 text-sm">Channel Balance</h4>
                            <div className="space-y-2">
                              {optimizationResult.channelBalance.map((cb) => (
                                <div key={cb.channel} className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getChannelInfo(cb.channel).color}`}>
                                    {cb.channel}
                                  </span>
                                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${
                                        cb.status === 'balanced' ? 'bg-green-500' :
                                        cb.status === 'understaffed' ? 'bg-amber-500' : 'bg-blue-500'
                                      }`}
                                      style={{ width: `${Math.min(cb.plannedPercentage, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-600 w-24 text-right">
                                    {cb.plannedPercentage.toFixed(0)}% / {cb.forecastPercentage.toFixed(0)}%
                                    {cb.variance !== 0 && (
                                      <span className={cb.variance > 0 ? 'text-blue-600' : 'text-amber-600'}>
                                        {' '}({cb.variance > 0 ? '+' : ''}{cb.variance.toFixed(0)}%)
                                      </span>
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">Planned % / Forecast %</p>
                          </div>
                        )}

                        {/* Schedule Continuity */}
                        {optimizationResult.scheduleContinuity && (
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3 text-sm flex items-center gap-2">
                              <Link2 className="h-4 w-4" />
                              Schedule Continuity
                            </h4>
                            <div className="flex items-center gap-2 mb-3">
                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-indigo-500 rounded-full"
                                  style={{ width: `${optimizationResult.scheduleContinuity.preservationPercentage}%` }}
                                />
                              </div>
                              <span className="text-sm font-semibold text-indigo-600">
                                {optimizationResult.scheduleContinuity.preservationPercentage.toFixed(0)}%
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs text-center">
                              <div className="p-2 bg-green-50 rounded">
                                <div className="font-semibold text-green-700">{optimizationResult.scheduleContinuity.agentImpact.noChange}</div>
                                <div className="text-green-600">No change</div>
                              </div>
                              <div className="p-2 bg-amber-50 rounded">
                                <div className="font-semibold text-amber-700">{optimizationResult.scheduleContinuity.agentImpact.minorChange}</div>
                                <div className="text-amber-600">±1 hour</div>
                              </div>
                              <div className="p-2 bg-red-50 rounded">
                                <div className="font-semibold text-red-700">{optimizationResult.scheduleContinuity.agentImpact.majorChange}</div>
                                <div className="text-red-600">Major change</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Rule Violations */}
                      {optimizationResult.ruleViolations && optimizationResult.ruleViolations.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h4 className="font-medium text-red-900 mb-2 flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Rule Violations ({optimizationResult.ruleViolations.length})
                          </h4>
                          <div className="space-y-2">
                            {optimizationResult.ruleViolations.slice(0, 3).map((violation, i) => (
                              <div key={i} className={`text-sm p-2 rounded ${
                                violation.severity === 'error' ? 'bg-red-100' : 'bg-amber-100'
                              }`}>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                    violation.severity === 'error' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'
                                  }`}>
                                    {violation.severity.toUpperCase()}
                                  </span>
                                  <span className="font-medium text-gray-900">{violation.ruleName}</span>
                                  {violation.shiftTime && (
                                    <span className="text-gray-500">@ {violation.shiftTime}</span>
                                  )}
                                </div>
                                <p className="text-gray-700 mt-1">{violation.reason}</p>
                                <p className="text-gray-500 text-xs mt-1">{violation.impact}</p>
                              </div>
                            ))}
                            {optimizationResult.ruleViolations.length > 3 && (
                              <p className="text-red-700 text-xs">
                                +{optimizationResult.ruleViolations.length - 3} more violations
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Gap Analysis */}
                      {optimizationResult.gapAnalysis && optimizationResult.gapAnalysis.uncoveredIntervals > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Coverage Gaps
                          </h4>
                          <p className="text-sm text-amber-700">
                            <span className="font-semibold">{optimizationResult.gapAnalysis.uncoveredIntervals}</span> uncovered interval{optimizationResult.gapAnalysis.uncoveredIntervals !== 1 ? 's' : ''} detected
                          </p>
                          {optimizationResult.gapAnalysis.recommendations && optimizationResult.gapAnalysis.recommendations.length > 0 && (
                            <ul className="text-amber-700 text-xs mt-2 space-y-1">
                              {optimizationResult.gapAnalysis.recommendations.map((rec: string, i: number) => (
                                <li key={i} className="flex items-start gap-1">
                                  <span className="text-amber-500">•</span>
                                  {rec}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {!isLoading && (
              <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-bold text-indigo-600">{shiftCounts.grouped}</span> shift{shiftCounts.grouped !== 1 ? 's' : ''}
                    <span className="text-gray-400 ml-1">
                      ({currentShifts.length} template{currentShifts.length !== 1 ? 's' : ''} × {shiftCounts.daysCount} day{shiftCounts.daysCount !== 1 ? 's' : ''})
                    </span>
                    {' '}| <span className="font-bold text-indigo-600">{peakConcurrentAgents}</span> peak agents needed
                    {' '}| <span className="font-semibold text-gray-700">{currentShifts.reduce((sum, s) => sum + s.agentsNeeded, 0)}</span> total across {currentShifts.length} shift{currentShifts.length !== 1 ? 's' : ''}
                    {avgOccupancy > 0 && (
                      <> | <span className="font-medium">{avgOccupancy}%</span> occupancy</>
                    )}
                  </div>
                  <div className="border-l border-gray-300 pl-4 flex items-center gap-2">
                    {/* Show selected channels */}
                    {config.channels.map(ch => (
                      <span key={ch} className={`px-2 py-0.5 rounded text-xs font-medium ${getChannelInfo(ch).color}`}>
                        {getChannelInfo(ch).label}
                      </span>
                    ))}
                  </div>
                  <div className="border-l border-gray-300 pl-4">
                    <DaysOfWeekBadge days={config.daysOfWeek} />
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={currentShifts.length === 0 || shiftCounts.grouped === 0}
                    className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    <span>Generate {shiftCounts.grouped} Shift{shiftCounts.grouped !== 1 ? 's' : ''}</span>
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
